import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, cn } from "../lib/api";
import TopBar from "../components/TopBar";
import { Button, Card, Input } from "../components/ui";
import { ArrowLeft, UserPlus, X, Play } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import PlayerPickerSheet from "../components/PlayerPickerSheet";
import { RoleTag } from "./ScoreLivePage";
import CoinToss from "../components/CoinToss";

const OVER_PRESETS = [5, 6, 8, 10, 12, 15, 20];

let _key = 0;
// Typed-in names (no member id) and the club's guest members are guests
const toPlayer = (m) => ({ key: `p${_key++}`, name: m.name, member_id: m.id || null, guest: !m.id || !!m.guest });
const canLead = (p) => p && !p.guest; // guests can't be captain or vice-captain

// Build a team and carry over its captain / vice-captain. `src` holds the ids the roles refer to
// (member ids for poll teams, player ids for the previous scorecard), in the same order as `players`.
const makeTeam = (name, players, src = [], captainId = null, viceId = null) => ({
  name,
  players,
  captain: canLead(players[src.indexOf(captainId)]) ? players[src.indexOf(captainId)].key : null,
  vice_captain: canLead(players[src.indexOf(viceId)]) ? players[src.indexOf(viceId)].key : null,
});

export default function ScoreNewPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const { club } = useAuth();
  const [members, setMembers] = useState([]);
  const [title, setTitle] = useState("");
  const [overs, setOvers] = useState(10);
  const [teams, setTeams] = useState([
    makeTeam("Team A", []),
    makeTeam("Team B", []),
  ]);
  const [common, setCommon] = useState([]); // uneven sides: players who play for both teams
  const [battingFirst, setBattingFirst] = useState(0);
  const [picking, setPicking] = useState(null); // team index, or "common"
  const [link, setLink] = useState({});
  const [comingIds, setComingIds] = useState(new Set()); // poll "IN" players, shown first in the picker
  const [pollTeams, setPollTeams] = useState(null); // lets the user reset to the poll's generated teams
  const [pollCommon, setPollCommon] = useState([]);
  const [fromLastMatch, setFromLastMatch] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const pollId = params.get("poll");
    const matchId = params.get("match");
    (async () => {
      try {
        const { data: mem } = await api.get("/members");
        setMembers(mem);
        const byId = (id) => mem.find((m) => m.id === id);
        if (pollId) {
          const [{ data: poll }, { data: last }, { data: dayMatches }] = await Promise.all([
            api.get(`/polls/${pollId}`),
            api.get("/scores/latest-lineup", { params: { poll_id: pollId } }),
            api.get("/scores", { params: { poll_id: pollId } }),
          ]);
          const side = (ids) => (ids || []).map(byId).filter(Boolean).map(toPlayer);
          const pollSide = (name, ids, cap, vc) => {
            const known = (ids || []).filter(byId);
            return makeTeam(name, side(known), known, cap, vc);
          };
          const fromPoll = [
            pollSide("Team A", poll.teams?.team_a, poll.teams?.captain_a, poll.teams?.vc_a),
            pollSide("Team B", poll.teams?.team_b, poll.teams?.captain_b, poll.teams?.vc_b),
          ];
          const fromPollCommon = side(poll.teams?.common ? [poll.teams.common] : []);
          setPollTeams(fromPoll);
          setPollCommon(fromPollCommon);
          setComingIds(new Set((poll.responses || []).filter((r) => r.status === "in").map((r) => r.member_id)));
          setTitle(`Match ${dayMatches.length + 1}`);
          if (last) {
            // Next match of the day starts from the previous lineup (guests included)
            setOvers(last.overs);
            // A common player is stored once per team; pull them back out into the common list
            setTeams(last.teams.map((t) => {
              const own = t.players.filter((p) => !p.common);
              return makeTeam(t.name, own.map((p) => toPlayer({ id: p.member_id, name: p.name, guest: p.guest })),
                own.map((p) => p.id), t.captain, t.vice_captain);
            }));
            setCommon(last.teams[0].players.filter((p) => p.common).map((p) => toPlayer({ id: p.member_id, name: p.name, guest: p.guest })));
            setFromLastMatch(true);
          } else {
            if (poll.overs) setOvers(poll.overs);
            setTeams(fromPoll);
            setCommon(fromPollCommon);
          }
          setLink({ poll_id: pollId });
        } else if (matchId) {
          const { data: match } = await api.get(`/matches/${matchId}`);
          setTitle(`${club?.name || "Us"} vs ${match.opponent}`);
          if (match.overs) setOvers(match.overs);
          setTeams([
            makeTeam(club?.name?.slice(0, 40) || "Us", (match.playing_xi || []).map(byId).filter(Boolean).map(toPlayer)),
            makeTeam(match.opponent?.slice(0, 40) || "Opponent", []),
          ]);
          setLink({ match_id: matchId });
        }
      } catch (err) { toast.error(apiError(err)); }
    })();
    // eslint-disable-next-line
  }, [loc.search]);

  const setTeam = (i, patch) => setTeams((ts) => ts.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const removePlayer = (i, key) => setTeam(i, {
    players: teams[i].players.filter((p) => p.key !== key),
    ...(teams[i].captain === key && { captain: null }),
    ...(teams[i].vice_captain === key && { vice_captain: null }),
  });
  // One person can't hold both roles: picking someone for one role clears the other
  const setRole = (i, role, key) => {
    const other = role === "captain" ? "vice_captain" : "captain";
    setTeam(i, { [role]: key || null, ...(key && teams[i][other] === key && { [other]: null }) });
  };
  const roleIndex = (t, key) => (key ? t.players.findIndex((p) => p.key === key) : -1);

  const takenIds = new Set([...teams.flatMap((t) => t.players), ...common].map((p) => p.member_id).filter(Boolean));

  const addPlayers = (i, picked) => {
    if (i === "common") setCommon((c) => [...c, ...picked.map(toPlayer)]);
    else setTeam(i, { players: [...teams[i].players, ...picked.map(toPlayer)] });
  };
  const asPayload = (p) => (p.member_id ? { member_id: p.member_id } : { name: p.name });

  const submit = async () => {
    if (teams.some((t) => !t.name.trim())) return toast.error("Give both teams a name");
    if (teams.some((t) => t.players.length + common.length < 2)) return toast.error("Each team needs at least 2 players");
    setSaving(true);
    try {
      const { data } = await api.post("/scores", {
        title: title.trim() || null,
        overs: Number(overs),
        batting_first: battingFirst,
        teams: teams.map((t) => ({
          name: t.name.trim(),
          players: t.players.map(asPayload),
          captain: roleIndex(t, t.captain) >= 0 ? roleIndex(t, t.captain) : null,
          vice_captain: roleIndex(t, t.vice_captain) >= 0 ? roleIndex(t, t.vice_captain) : null,
        })),
        common_players: common.map(asPayload),
        ...link,
      });
      toast.success("Match started — let's score!");
      nav(`/scores/${data.id}`, { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pb-28">
      <div className="px-3 pt-4">
        <button onClick={() => nav(-1)} className="h-10 w-10 rounded-full hover:bg-black/5 grid place-items-center" data-testid="back-btn">
          <ArrowLeft size={20} />
        </button>
      </div>
      <TopBar title="New scorecard" subtitle="Live scoring" showClubSwitcher={false} />

      <div className="px-5 space-y-4">
        {fromLastMatch && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-brand-50 text-brand-700 text-sm">
            <span>Teams copied from the last match. Remove players who left, add any new ones.</span>
            {pollTeams && (
              <button
                type="button"
                className="font-semibold underline shrink-0"
                onClick={() => { setTeams(pollTeams); setCommon(pollCommon); setFromLastMatch(false); }}
                data-testid="reset-poll-teams-btn"
              >
                Use poll teams
              </button>
            )}
          </div>
        )}
        <Card className="space-y-4">
          <Input label="Match title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Sunday friendly" data-testid="score-title" />
          <div>
            <span className="text-xs uppercase tracking-widest text-ink-muted font-semibold block mb-1.5">Overs per side</span>
            <div className="flex flex-wrap gap-2">
              {OVER_PRESETS.map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => setOvers(o)}
                  className={cn("h-10 min-w-[44px] px-3 rounded-xl text-sm font-bold", Number(overs) === o ? "bg-brand text-white" : "bg-black/[0.04]")}
                >
                  {o}
                </button>
              ))}
              <input
                type="number"
                min="1"
                max="50"
                value={overs}
                onChange={(e) => setOvers(e.target.value)}
                className="h-10 w-16 px-2 rounded-xl border border-black/10 text-center font-bold"
                aria-label="Custom overs"
                data-testid="score-overs"
              />
            </div>
          </div>
        </Card>

        {teams.map((t, i) => (
          <Card key={i} className="space-y-3" data-testid={`score-team-${i}`}>
            <Input label={`Team ${i + 1}`} value={t.name} maxLength={40} onChange={(e) => setTeam(i, { name: e.target.value })} data-testid={`score-team-name-${i}`} />
            <div className="flex flex-wrap gap-2">
              {t.players.map((p) => (
                <span key={p.key} className="inline-flex items-center gap-1 h-9 pl-3 pr-1 rounded-xl bg-black/[0.04] text-sm font-semibold">
                  {p.name}
                  <RoleTag role={p.key === t.captain ? "C" : p.key === t.vice_captain ? "VC" : null} />
                  {p.guest && <span className="text-[10px] uppercase text-amber-700 font-bold">guest</span>}
                  <button type="button" onClick={() => removePlayer(i, p.key)} className="h-7 w-7 grid place-items-center rounded-lg hover:bg-black/10" aria-label={`Remove ${p.name}`}>
                    <X size={14} />
                  </button>
                </span>
              ))}
              <button
                type="button"
                onClick={() => setPicking(i)}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border border-dashed border-brand text-brand text-sm font-semibold"
                data-testid={`score-add-players-${i}`}
              >
                <UserPlus size={14} /> Add players
              </button>
            </div>
            {t.players.some(canLead) && (
              <div className="grid grid-cols-2 gap-2">
                {[["captain", "Captain"], ["vice_captain", "Vice captain"]].map(([role, label]) => (
                  <label key={role} className="min-w-0">
                    <span className="text-[10px] uppercase tracking-widest text-ink-muted font-semibold block mb-1">{label}</span>
                    <select
                      value={t[role] || ""}
                      onChange={(e) => setRole(i, role, e.target.value)}
                      className="w-full h-10 px-2 rounded-xl bg-black/[0.04] text-sm font-semibold outline-none"
                      data-testid={`score-team-${i}-${role}`}
                    >
                      <option value="">None</option>
                      {t.players.filter(canLead).map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
                    </select>
                  </label>
                ))}
              </div>
            )}
            <div className="text-xs text-ink-muted">
              {t.players.length} players{common.length > 0 && ` + ${common.length} common`}
            </div>
          </Card>
        ))}

        <Card className="space-y-3" data-testid="score-common">
          <div>
            <span className="text-xs uppercase tracking-widest text-ink-muted font-semibold block">Common players</span>
            <p className="text-xs text-ink-muted mt-1">Uneven sides? A common player plays for both teams — bats and bowls for either side.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {common.map((p) => (
              <span key={p.key} className="inline-flex items-center gap-1 h-9 pl-3 pr-1 rounded-xl bg-sky-50 text-sky-800 text-sm font-semibold">
                {p.name}
                <span className="text-[10px] uppercase font-bold">common</span>
                {p.guest && <span className="text-[10px] uppercase text-amber-700 font-bold">guest</span>}
                <button type="button" onClick={() => setCommon((c) => c.filter((x) => x.key !== p.key))} className="h-7 w-7 grid place-items-center rounded-lg hover:bg-black/10" aria-label={`Remove ${p.name}`}>
                  <X size={14} />
                </button>
              </span>
            ))}
            <button
              type="button"
              onClick={() => setPicking("common")}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border border-dashed border-sky-600 text-sky-700 text-sm font-semibold"
              data-testid="score-add-common"
            >
              <UserPlus size={14} /> Add common player
            </button>
          </div>
        </Card>

        <Card>
          <CoinToss teams={teams} battingFirst={battingFirst} onChoice={setBattingFirst} />
          <span className="text-xs uppercase tracking-widest text-ink-muted font-semibold block mb-2">Who bats first?</span>
          <div className="grid grid-cols-2 gap-2 p-1 bg-black/[0.04] rounded-2xl">
            {teams.map((t, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setBattingFirst(i)}
                className={cn("h-11 rounded-xl text-sm font-semibold truncate px-2 transition-colors", battingFirst === i ? "bg-brand text-white shadow-sm" : "text-ink-muted")}
                aria-pressed={battingFirst === i}
                data-testid={`bat-first-${i}`}
              >
                {t.name || `Team ${i + 1}`}
              </button>
            ))}
          </div>
        </Card>

        <Button size="lg" className="w-full" onClick={submit} disabled={saving} data-testid="score-start-btn">
          <Play size={18} /> {saving ? "Starting…" : "Start scoring"}
        </Button>
      </div>

      <PlayerPickerSheet
        open={picking !== null}
        onClose={() => setPicking(null)}
        title={picking === "common" ? "Add common player" : picking !== null ? `Add to ${teams[picking].name || "team"}` : ""}
        members={members.filter((m) => !takenIds.has(m.id))}
        priorityIds={comingIds}
        onDone={(picked) => { addPlayers(picking, picked); setPicking(null); }}
      />
    </div>
  );
}
